import { useId } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_ROLE_COLORS } from '@/theme/chartPalette';
import {
  CHART_AXIS_TICK,
  CHART_LEGEND_STYLE,
  CHART_TOOLTIP_CONTENT_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  DEFAULT_CHART_HEIGHT,
  buildLabelFormatter,
  buildValueFormatter,
} from './chartStyles';

export interface TrendAreaSeries {
  key: string;
  label: string;
  color: string;
}

interface TrendAreaChartProps {
  data: Array<Record<string, string | number>>;
  xKey: string;
  series: TrendAreaSeries[];
  height?: number;
  accessibleLabel: string;
  formatXTick: (value: string) => string;
  formatValue: (value: number) => string;
}

export function TrendAreaChart({
  data,
  xKey,
  series,
  height = DEFAULT_CHART_HEIGHT,
  accessibleLabel,
  formatXTick,
  formatValue,
}: TrendAreaChartProps) {
  const gradientPrefix = useId();
  return (
    <div role="img" aria-label={accessibleLabel} className="w-full">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <defs>
            {series.map(function renderGradient(entry) {
              return (
                <linearGradient
                  key={entry.key}
                  id={gradientPrefix + entry.key}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={entry.color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={entry.color} stopOpacity={0.02} />
                </linearGradient>
              );
            })}
          </defs>
          <CartesianGrid stroke={CHART_ROLE_COLORS.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey={xKey}
            tickFormatter={formatXTick}
            tick={CHART_AXIS_TICK}
            axisLine={{ stroke: CHART_ROLE_COLORS.grid }}
            tickLine={false}
            minTickGap={24}
          />
          <YAxis
            tickFormatter={formatValue}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            width={36}
            allowDecimals={false}
          />
          <Tooltip
            formatter={buildValueFormatter(formatValue)}
            labelFormatter={buildLabelFormatter(formatXTick)}
            contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
            labelStyle={CHART_TOOLTIP_LABEL_STYLE}
            itemStyle={CHART_TOOLTIP_ITEM_STYLE}
          />
          {series.length > 1 ? (
            <Legend wrapperStyle={CHART_LEGEND_STYLE} iconType="circle" iconSize={9} />
          ) : null}
          {series.map(function renderArea(entry) {
            return (
              <Area
                key={entry.key}
                type="monotone"
                dataKey={entry.key}
                name={entry.label}
                stroke={entry.color}
                strokeWidth={2}
                fill={'url(#' + gradientPrefix + entry.key + ')'}
                dot={false}
                activeDot={{ r: 4 }}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
